import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { ShieldOff } from "lucide-react";
import { useState } from "react";
import {
  Create,
  Datagrid,
  DateField,
  Edit,
  FormDataConsumer,
  List,
  NumberField,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  required,
  useNotify,
  useRecordContext,
  useRedirect,
} from "react-admin";
import { authClient } from "../../server";
import { bulkEmailResources } from "./common";

const suppressionFilters = [
  <TextInput key="email" source="email" alwaysOn />,
  <SelectInput
    key="scope"
    source="scope"
    choices={[
      { id: "global", name: "Global" },
      { id: "campaign", name: "Campaign" },
    ]}
  />,
];

function SuppressionList() {
  return (
    <List filters={suppressionFilters} sort={{ field: "id", order: "DESC" }}>
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" />
        <TextField source="normalized_email" label="Email" />
        <TextField source="action" />
        <TextField source="scope" />
        <NumberField source="campaign_id" />
        <TextField source="source" />
        <TextField source="reason" />
        <DateField source="created_at" showTime />
      </Datagrid>
    </List>
  );
}

function CampaignScopeInput({ scopedFormData }) {
  if (scopedFormData.scope !== "campaign") return null;
  return (
    <ReferenceInput
      source="campaign_id"
      reference={bulkEmailResources.campaigns}
    >
      <SelectInput optionText="name" validate={required()} />
    </ReferenceInput>
  );
}

function SuppressionCreate() {
  return (
    <Create
      redirect="list"
      mutationMode="pessimistic"
      transform={(data) => ({
        ...data,
        campaign_id:
          data.scope === "campaign" ? Number(data.campaign_id) : null,
      })}
    >
      <SimpleForm defaultValues={{ scope: "global", action: "suppress" }}>
        <TextInput source="email" type="email" validate={required()} />
        <SelectInput
          source="scope"
          validate={required()}
          choices={[
            { id: "global", name: "Global" },
            { id: "campaign", name: "Campaign" },
          ]}
        />
        <FormDataConsumer>
          {({ formData }) => <CampaignScopeInput scopedFormData={formData} />}
        </FormDataConsumer>
        <SelectInput
          source="action"
          validate={required()}
          choices={[
            { id: "suppress", name: "Suppress" },
            { id: "unsuppress", name: "Unsuppress" },
          ]}
        />
        <TextInput source="reason" fullWidth multiline minRows={2} />
      </SimpleForm>
    </Create>
  );
}

// "Editing" an append-only suppression records a later event instead of rewriting audit history.
function SuppressionEventEditor() {
  const record = useRecordContext();
  const notify = useNotify();
  const redirect = useRedirect();
  const [action, setAction] = useState(
    record.action === "suppress" ? "unsuppress" : "suppress",
  );
  const [reason, setReason] = useState("");

  const appendEvent = async () => {
    try {
      await authClient.post("/admin/bulk-email/suppressions", {
        email: record.normalized_email,
        scope: record.scope,
        campaign_id: record.campaign_id,
        action,
        reason,
      });
      notify("Suppression event recorded", { type: "success" });
      redirect("list", bulkEmailResources.suppressions);
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Append a new suppression event
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Existing events are immutable. This records a new event for{" "}
        {record.normalized_email} in the {record.scope} scope
        {record.campaign_id ? ` (campaign ${record.campaign_id})` : ""}.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Action</InputLabel>
          <Select
            label="Action"
            value={action}
            onChange={(event) => setAction(event.target.value)}
          >
            <MenuItem value="suppress">Suppress</MenuItem>
            <MenuItem value="unsuppress">Unsuppress</MenuItem>
          </Select>
        </FormControl>
        <MuiTextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          sx={{ minWidth: 360 }}
        />
        <Button variant="contained" onClick={appendEvent}>
          Record event
        </Button>
      </Box>
    </Box>
  );
}

function SuppressionEdit() {
  return (
    <Edit mutationMode="pessimistic">
      <SuppressionEventEditor />
    </Edit>
  );
}

export const BulkEmailSuppressions = {
  category: "email",
  name: bulkEmailResources.suppressions,
  list: SuppressionList,
  create: SuppressionCreate,
  edit: SuppressionEdit,
  icon: ShieldOff,
  roles: ["administrator"],
  options: { label: "Email Suppressions" },
};
